import React, { useEffect, useState } from 'react';
import {
  Box,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
} from '@mui/material';
import { SEGMENT_TYPE } from '../../../../../../constants/radar.js';
import {
  SEGMENT_PROCESS_APPLIES_TO,
  SEGMENT_PROCESS_APPLIES_TO_OPTIONS,
  COMPETENCY_ASSIGNMENT_STATUS,
  COMPETENCY_ASSIGNMENT_OPTIONS,
  COMPETENCY_VISIBILITY_STATUS,
  COMPETENCY_VISIBILITY_OPTIONS,
  SEGMENT_VISIBILITY_STATUS,
  SEGMENT_VISIBILITY_OPTIONS,
} from '../../../../../../constants/segmentProcess';
import Modal, { Field, Label } from '../../../../../common/Modal';
import EditableItemList from '../../../../../MyActivities/MyCV/components/common/EditableItemList';
import NodeTypeSelector from './NodeTypeSelector';
import NodeColorPicker, { PREDEFINED_COLORS } from './NodeColorPicker';
import ApprovalStageSelector from './ApprovalStageSelector';
import NodeTags from './NodeTags';

import {
  TabContainer,
  TabButton,
} from './styles';

const NodeModal = ({ isOpen, onClose, onSubmit, node, mode }) => {
  const defaultFormData = {
    name: '',
    approvalStage: '',
    description: '',
    tags: [],
    color: PREDEFINED_COLORS[0],
    nodeType: SEGMENT_TYPE.LEAF,
    processes: [],
  };

  const [formData, setFormData] = useState(defaultFormData);
  const [activeTab, setActiveTab] = useState('details');
  const [expandedProcessIndex, setExpandedProcessIndex] = useState(null);

  // Reset form when modal opens/closes
  useEffect(() => {
    if (!isOpen) {
      setFormData(defaultFormData);
    } else if (mode === 'add') {
      setFormData(defaultFormData);
    } else if (mode === 'edit' && node) {
      setFormData({
        name: node.name || '',
        approvalStage: node.approvalStage || '',
        description: node.description || '',
        tags: node.tags || [],
        color: node.color || PREDEFINED_COLORS[0],
        nodeType: node.segmentType || SEGMENT_TYPE.LEAF,
        processes: node.segmentProcesses || [],
      });
    }
  }, [isOpen, mode, node]);

  const handleSubmit = () => {
    onSubmit(formData);
    onClose();
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    // Handle approval stage deselection
    if (name === 'approvalStage' && formData.approvalStage === value) {
      setFormData({
        ...formData,
        approvalStage: '' // Clear the approval stage
      });
      return;
    }

    // Handle normal input changes
    setFormData({
      ...formData,
      [name]: value
    });
  };

  const handleTagToggle = (tagId, tagName) => {
    const isSelected = formData.tags.some(tag => tag.id === tagId);
    setFormData(prev => ({
      ...prev,
      tags: isSelected
        ? prev.tags.filter(tag => tag.id !== tagId)
        : [...prev.tags, { id: tagId, name: tagName }]
    }));
  };

  const handleColorSelect = (color) => {
    setFormData(prev => ({
      ...prev,
      color
    }));
  };

  const handleNodeTypeChange = (e) => {
    const newNodeType = e.target.value;
    setFormData(prev => ({
      ...prev,
      nodeType: newNodeType,
      ...(newNodeType === SEGMENT_TYPE.INTERNAL ? { approvalStage: '', tags: [] } : { color: PREDEFINED_COLORS[0] }),
    }));
  };

  // Process management handlers
  const getAvailableAppliesToOptions = (currentProcess = null, processList = formData.processes) => {
    const usedTypes = processList
      .filter(p => !currentProcess || p.appliesTo !== currentProcess.appliesTo)
      .map(p => p.appliesTo);
    return SEGMENT_PROCESS_APPLIES_TO_OPTIONS.filter(opt => !usedTypes.includes(opt.value));
  };

  const createNewProcess = (currentProcesses = []) => ({
    tempId: `new-${Date.now()}`,
    isNew: true,
    appliesTo: getAvailableAppliesToOptions(null, currentProcesses)[0]?.value || SEGMENT_PROCESS_APPLIES_TO.CV,
    competencyAssignment: COMPETENCY_ASSIGNMENT_STATUS.DISABLED,
    competencyVisibility: COMPETENCY_VISIBILITY_STATUS.DISABLED,
    segmentVisibility: SEGMENT_VISIBILITY_STATUS.ENABLED,
  });

  const handleProcessesChange = (newProcesses) => {
    setFormData(prev => ({
      ...prev,
      processes: newProcesses
    }));
  };

  const handleDeleteProcess = (index) => {
    const newProcesses = [...formData.processes];
    newProcesses.splice(index, 1);
    setFormData(prev => ({
      ...prev,
      processes: newProcesses
    }));
  };

  const renderProcessSummary = (process) => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
        {process.appliesTo}
      </Typography>
      <Box sx={{ display: 'flex', gap: 2, fontSize: '12px', color: 'text.secondary', flexWrap: 'wrap' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          Assignment: {process.competencyAssignment}
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          Competency Visibility: {process.competencyVisibility}
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          Segment Visibility: {process.segmentVisibility}
        </Box>
      </Box>
    </Box>
  );

  const renderProcessEditForm = (process, onChange) => {
    // For new processes, show available options (excluding those already used by other processes)
    const availableOptions = getAvailableAppliesToOptions(process);
    const processKey = process.id || process.tempId;

    return (
      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
        <FormControl size="small" sx={{ minWidth: 120, flex: 1 }}>
          <InputLabel id={`applies-to-label-${processKey}`}>Applies To</InputLabel>
          <Select
            labelId={`applies-to-label-${processKey}`}
            value={process.appliesTo}
            label="Applies To"
            onChange={(e) => onChange({ ...process, appliesTo: e.target.value })}
            disabled={!process.isNew}
          >
            {availableOptions.map(opt => (
              <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 150, flex: 1 }}>
          <InputLabel id={`competency-assignment-label-${processKey}`}>Competency Assignment</InputLabel>
          <Select
            labelId={`competency-assignment-label-${processKey}`}
            value={process.competencyAssignment}
            label="Competency Assignment"
            onChange={(e) => onChange({ ...process, competencyAssignment: e.target.value })}
          >
            {COMPETENCY_ASSIGNMENT_OPTIONS.map(opt => (
              <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 150, flex: 1 }}>
          <InputLabel id={`competency-visibility-label-${processKey}`}>Competency Visibility</InputLabel>
          <Select
            labelId={`competency-visibility-label-${processKey}`}
            value={process.competencyVisibility}
            label="Competency Visibility"
            onChange={(e) => onChange({ ...process, competencyVisibility: e.target.value })}
          >
            {COMPETENCY_VISIBILITY_OPTIONS.map(opt => (
              <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 150, flex: 1 }}>
          <InputLabel id={`segment-visibility-label-${processKey}`}>Segment Visibility</InputLabel>
          <Select
            labelId={`segment-visibility-label-${processKey}`}
            value={process.segmentVisibility || SEGMENT_VISIBILITY_STATUS.ENABLED}
            label="Segment Visibility"
            onChange={(e) => onChange({ ...process, segmentVisibility: e.target.value })}
          >
            {SEGMENT_VISIBILITY_OPTIONS.map(opt => (
              <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>
    );
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      onSubmit={handleSubmit}
      title={mode === 'add' ? 'Add New Node' : 'Edit Node'}
      submitText={mode === 'add' ? 'Add' : 'Save'}
      maxWidth="lg"
    >
      <Field>
        <Label>Node Type</Label>
        <NodeTypeSelector
          selectedType={formData.nodeType}
          onTypeChange={(type) => handleNodeTypeChange({ target: { value: type }})}
          disabled={mode === 'edit'}
        />
      </Field>

      {formData.nodeType === SEGMENT_TYPE.INTERNAL ? (
        <>
          <TabContainer>
            <TabButton
              type="button"
              active={activeTab === 'details'}
              onClick={() => setActiveTab('details')}
            >
              Details
            </TabButton>
            {mode === 'edit' && (
              <TabButton
                type="button"
                active={activeTab === 'process'}
                onClick={() => setActiveTab('process')}
              >
                Process
              </TabButton>
            )}
          </TabContainer>

          {activeTab === 'details' && (
            <>
              <Box mb={2}>
                <TextField
                  id="name"
                  name="name"
                  label="Name"
                  required
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="Enter node name"
                  autoComplete="off"
                  fullWidth
                  variant="outlined"
                  size="small"
                />
              </Box>

              <Box mb={2}>
                <TextField
                  id="description"
                  name="description"
                  label="Description"
                  value={formData.description}
                  onChange={handleChange}
                  placeholder="Enter a description..."
                  autoComplete="off"
                  fullWidth
                  variant="outlined"
                  size="small"
                  multiline
                  rows={3}
                />
              </Box>

              <NodeColorPicker
                selectedColor={formData.color}
                onColorSelect={handleColorSelect}
              />
            </>
          )}

          {activeTab === 'process' && mode === 'edit' && (
            <Field>
              <EditableItemList
                items={formData.processes}
                setItems={handleProcessesChange}
                expandedIndex={expandedProcessIndex}
                setExpandedIndex={setExpandedProcessIndex}
                createNewItem={createNewProcess}
                renderSummary={renderProcessSummary}
                renderEditForm={renderProcessEditForm}
                addButtonText="Add Process"
                emptyState={{
                  title: "No processes configured",
                  description: "Add a process to define how this segment handles competencies."
                }}
                itemName="process"
                onDelete={handleDeleteProcess}
                disableAdd={getAvailableAppliesToOptions().length === 0}
              />
            </Field>
          )}
        </>
      ) : (
        <>
          <TabContainer>
            <TabButton
              type="button"
              active={activeTab === 'details'}
              onClick={() => setActiveTab('details')}
            >
              Details
            </TabButton>
            <TabButton
              type="button"
              active={activeTab === 'tags'}
              onClick={() => setActiveTab('tags')}
            >
              Tags
            </TabButton>
          </TabContainer>

          {activeTab === 'details' && (
            <>
              <Box mb={2}>
                <TextField
                  id="name"
                  name="name"
                  label="Name"
                  required
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="Enter node name"
                  autoComplete="off"
                  fullWidth
                  variant="outlined"
                  size="small"
                />
              </Box>

              <Box mb={2}>
                <TextField
                  id="description"
                  name="description"
                  label="Description"
                  value={formData.description}
                  onChange={handleChange}
                  placeholder="Enter a description..."
                  autoComplete="off"
                  fullWidth
                  variant="outlined"
                  size="small"
                  multiline
                  rows={3}
                />
              </Box>

              <ApprovalStageSelector
                selectedStage={formData.approvalStage}
                onStageChange={(stage) => setFormData(prev => ({ ...prev, approvalStage: stage }))}
              />
            </>
          )}

          {activeTab === 'tags' && (
            <NodeTags
              selectedTags={formData.tags}
              onTagToggle={handleTagToggle}
              isOpen={isOpen}
            />
          )}
        </>
      )}
    </Modal>
  );
};

export default NodeModal;
